/**
 * Real camera for the capture session, with graceful fallback.
 *
 * On devices with a camera (and permission granted), the viewfinder shows
 * the live rear-camera stream and the shutter grabs real frames - this is
 * what de-risks the Mini PRD's "PWA camera friction" constraint when the
 * prototype is opened on a phone.
 *
 * Anywhere without a camera (desktop without webcam, permission denied,
 * Storybook in CI) it reports 'unavailable' and the capture screen falls
 * back to the simulated viewfinder.
 */
import { useEffect, useRef, useState } from 'react';

export type CameraState = 'starting' | 'live' | 'unavailable';

export const useCamera = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [state, setState] = useState<CameraState>('starting');

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setState('unavailable');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setState('live');
      } catch {
        if (!cancelled) setState('unavailable');
      }
    };

    start();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  /** Grab the current frame as a JPEG data URL, or null in fallback mode. */
  const captureFrame = (): string | null => {
    const video = videoRef.current;
    if (state !== 'live' || !video || !video.videoWidth) return null;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.8);
  };

  return { videoRef, state, captureFrame };
};
